/**
 * Parameters panel — the document's named parameter table (ƒx).
 * Each row: name = expression → value. Expressions may reference other
 * parameters ("width/2 + 5"); every edit is an undo step and regenerates
 * all features that use the parameter.
 */

import { evalParamTable } from '@core/expr';
import type { App } from '../app';

export function mountParamsPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  const render = (): void => {
    const open = app.activePanel === 'params';
    panel.style.display = open ? 'block' : 'none';
    if (!open) return;
    panel.innerHTML = '<h3>Parameters</h3>';

    const table = app.doc.timeline.params;
    const { values, errors } = evalParamTable(table);
    const names = Object.keys(table).sort();

    if (names.length === 0) {
      panel.insertAdjacentHTML(
        'beforeend',
        '<div class="side-status">No parameters yet. Add one below, then use it anywhere a number goes — extrude distance, dimensions… e.g. "width/2".</div>',
      );
    }

    for (const name of names) {
      const row = document.createElement('div');
      row.className = 'param-row';
      const label = document.createElement('span');
      label.className = 'param-name';
      label.textContent = name;
      label.title = name;
      const input = document.createElement('input');
      input.type = 'text';
      input.value = table[name]!;
      input.addEventListener('change', () => {
        if (input.value.trim()) void app.setParameter(name, input.value);
      });
      const value = document.createElement('span');
      value.className = 'param-value';
      if (name in errors) {
        value.textContent = '⚠';
        value.title = errors[name]!;
        value.classList.add('err');
      } else {
        const v = values[name]!;
        value.textContent = `= ${Math.round(v * 1000) / 1000}`;
      }
      const del = document.createElement('button');
      del.className = 'chat-mini danger';
      del.textContent = '✕';
      del.title = `Delete ${name}`;
      del.addEventListener('click', () => void app.deleteParameter(name));
      row.append(label, input, value, del);
      panel.appendChild(row);
    }

    // Add-new row.
    const add = document.createElement('div');
    add.className = 'param-row param-add';
    const nameIn = document.createElement('input');
    nameIn.type = 'text';
    nameIn.placeholder = 'name';
    nameIn.className = 'param-name-input';
    const exprIn = document.createElement('input');
    exprIn.type = 'text';
    exprIn.placeholder = 'expression, e.g. 60 or width/2';
    const addBtn = document.createElement('button');
    addBtn.className = 'chat-mini';
    addBtn.textContent = 'Add';
    const submit = (): void => {
      const name = nameIn.value.trim();
      const expr = exprIn.value.trim();
      if (!name || !expr) return;
      app.setParameter(name, expr).catch((err: unknown) => {
        app.setIoStatus(err instanceof Error ? err.message : String(err));
      });
      nameIn.value = '';
      exprIn.value = '';
    };
    addBtn.addEventListener('click', submit);
    exprIn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });
    add.append(nameIn, exprIn, addBtn);
    panel.appendChild(add);

    panel.insertAdjacentHTML(
      'beforeend',
      '<div class="side-status">Use in any numeric field or dimension: type the expression instead of a number. Functions: sin/cos/tan (degrees), sqrt, abs, min, max, round; constant pi.</div>',
    );
  };

  app.onUI(render);
  render();
}
